import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getMSectionVO,
    saveMSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    M2_PAGE,
    MAPP01_PAGE,
    MS_PAGE,
    M_PAGE_TITLES,
    M_SECTION_INDEX,
    M_SECTION_INDEX_LETTER,
    M_SECTION_INDEX_TITLE,
    M_SECTION_PAGES,
    MProgrammePage,
    MSectionPageCode,
    MSummaryPage,
} from '@/pages/components/audit-program/m-section/constants';
import MsSummaryView from '@/pages/components/audit-program/m-section/MsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type MSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: MSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<MSectionPageCode>(['Mi']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: MSectionPageCode): boolean {
    return code === 'M2' || code === 'Mapp01';
}

export default function MSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Mi',
}: MSectionViewProps) {
    const [pageKey, setPageKey] = useState<MSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('M');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getMSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of M_SECTION_PAGES) {
                if (code === 'Mi') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load M Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: MSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveMSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Mi') as {rows?: typeof M_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : M_SECTION_INDEX;
    }, [getContent]);

    const msContent = (getContent('Ms') ?? MS_PAGE) as MSummaryPage;
    const m2Content = (getContent('M2') ?? M2_PAGE) as MProgrammePage;
    const mapp01Content = (getContent('Mapp01') ?? MAPP01_PAGE) as MProgrammePage;

    const tabs = useMemo(() => M_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={M_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Mi' ? (
                <SectionIndexView
                    header={header}
                    refCode="Mi"
                    sectionLetter={M_SECTION_INDEX_LETTER}
                    sectionTitle={M_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as MSectionPageCode))}
                />
            ) : code === 'Ms' ? (
                <MsSummaryView
                    header={header}
                    page={msContent}
                    refCode="Ms"
                    values={pageData.Ms ?? {}}
                    onChange={(k, v) => handleFieldChange('Ms', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={code === 'M2' ? m2Content : mapp01Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : null
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, msContent, m2Content, mapp01Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    M Section — Long-term loans and deferred income
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as MSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
